import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import CatalogCategoryOnboardingBanner from "../../../../Components/Studio/CatalogCategory/CatalogCategoryOnboardingBanner";
import { HELP_ARTICLE_PRODUCTS } from "../../../../helpers/helpCenter";
import CatalogCategoryWizard from "../../../../Components/Studio/CatalogCategory/CatalogCategoryWizard";
import SmartImportWizard from "../../../../Components/Studio/SmartImportWizard";
import ProductNfeImportOffcanvas from "../../../../Components/Studio/ProductNfeImportOffcanvas";
import MoneyInput from "../../../../Components/Common/MoneyInput";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import { groupCatalogItemsByCategory } from "../../../../helpers/catalogCategoryGrouping";
import { resolveCategoryLabel } from "../../../../helpers/catalogCategoryDisplay";
import { fetchAllPaginated } from "../../../../helpers/fetchAllPaginated";
import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import {
  createProduct,
  CreateProductPayload,
  deleteProduct,
  deleteProductPhoto,
  getProduct,
  getProducts,
  Product,
  ProductType,
  UnitOfMeasure,
  updateProduct,
  uploadProductPhoto,
} from "../../../../services/productService";
import {
  getMetaCommerceProductMessage,
  getMetaCommerceStatus,
} from "../../../../services/integrationService";
import { CatalogSyncLabel } from "../../../../types/product";
import { copyTextToClipboard } from "../../../../helpers/clipboard";
import { getCatalogCategories } from "../../../../services/catalogCategoryService";
import { getSettings } from "../../../../services/tenantSettingService";
import { getUnits, Unit } from "../../../../services/unitService";
import { userHasAnyRole } from "../../../../services/authService";
import { useCatalogCategoryOnboarding } from "../../../../hooks/useCatalogCategoryOnboarding";
import { PaginationMeta } from "../../../../types/api";
import { CatalogCategory } from "../../../../types/catalogCategory";

import ProductStockHistoryPanel from "./ProductStockHistoryPanel";
import ProductBatchesPanel from "./ProductBatchesPanel";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;
const UNITS_PAGE_SIZE = 100;
const CATEGORIES_PAGE_SIZE = 100;

type ProductFormTab = "data" | "stock" | "batches";

interface ProductFormValues {
  unit_id: string;
  name: string;
  brand: string;
  supplier_name: string;
  product_type: ProductType;
  unit_of_measure: UnitOfMeasure;
  current_stock: number;
  minimum_stock: number;
  sale_price_display: string;
  cost_price_display: string;
  open_shelf_life_days: string;
  category_id: string;
}

const emptyFormValues = (): ProductFormValues => ({
  unit_id: "",
  name: "",
  brand: "",
  supplier_name: "",
  product_type: "retail",
  unit_of_measure: "unit",
  current_stock: 0,
  minimum_stock: 0,
  sale_price_display: "",
  cost_price_display: "",
  open_shelf_life_days: "",
  category_id: "",
});

const mapProductToFormValues = (
  product: Product,
  locale: string
): ProductFormValues => ({
  unit_id: String(product.unit_id),
  name: product.name,
  brand: product.brand ?? "",
  supplier_name: product.supplier_name ?? "",
  product_type: product.product_type,
  unit_of_measure: product.unit_of_measure,
  current_stock: product.current_stock,
  minimum_stock: product.minimum_stock,
  sale_price_display:
    product.sale_price !== null
      ? formatCentsToMoneyInput(product.sale_price, locale)
      : "",
  cost_price_display:
    product.cost_price_cents !== null
      ? formatCentsToMoneyInput(product.cost_price_cents, locale)
      : "",
  open_shelf_life_days:
    product.open_shelf_life_days != null
      ? String(product.open_shelf_life_days)
      : "",
  category_id: product.category_id ? String(product.category_id) : "",
});

const buildProductPayload = (
  values: ProductFormValues,
  useCatalogCategories: boolean,
  locale: string
): CreateProductPayload => ({
  unit_id: Number(values.unit_id),
  name: values.name.trim(),
  brand: values.brand.trim() !== "" ? values.brand.trim() : null,
  supplier_name:
    values.supplier_name.trim() !== "" ? values.supplier_name.trim() : null,
  product_type: values.product_type,
  unit_of_measure: values.unit_of_measure,
  current_stock: Number(values.current_stock),
  minimum_stock: Number(values.minimum_stock),
  sale_price:
    values.product_type === "retail"
      ? parseMoneyInputToCents(values.sale_price_display, locale)
      : null,
  cost_price_cents:
    values.cost_price_display.trim() !== ""
      ? parseMoneyInputToCents(values.cost_price_display, locale)
      : null,
  open_shelf_life_days:
    values.open_shelf_life_days.trim() !== ""
      ? Number(values.open_shelf_life_days)
      : null,
  ...(useCatalogCategories
    ? { category_id: values.category_id ? Number(values.category_id) : null }
    : {}),
});

const resolveProductMargin = (product: Product): number | null => {
  if (
    product.product_type !== "retail" ||
    product.sale_price === null ||
    product.sale_price <= 0 ||
    product.cost_price_cents === null ||
    product.cost_price_cents <= 0
  ) {
    return null;
  }

  return (product.sale_price - product.cost_price_cents) / product.sale_price;
};

const resolveProductCatalogMeta = (product: Product): string | null => {
  const parts = [product.brand, product.supplier_name].filter(Boolean);

  return parts.length > 0 ? parts.join(" · ") : null;
};

const ProductsList = () => {
  const { t, i18n } = useTranslation();

  document.title = t("studio.products.pageTitle");

  const canConfigureCategories = userHasAnyRole(["owner"]);

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const formatPercent = useCallback(
    (ratio: number) =>
      new Intl.NumberFormat(i18n.language, {
        style: "percent",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(ratio),
    [i18n.language]
  );

  const [products, setProducts] = useState<Product[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [debouncedBrandFilter, setDebouncedBrandFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [debouncedSupplierFilter, setDebouncedSupplierFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [openedOnly, setOpenedOnly] = useState(false);
  const [missingPhotoOnly, setMissingPhotoOnly] = useState(false);
  const [catalogMissingPhotoCount, setCatalogMissingPhotoCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [useCatalogCategories, setUseCatalogCategories] = useState(false);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [productFormTab, setProductFormTab] = useState<ProductFormTab>("data");
  const [negativeMarginModal, setNegativeMarginModal] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] =
    useState<ProductFormValues | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [nfeImportOpen, setNfeImportOpen] = useState(false);

  const canAdjustStock = userHasAnyRole(["owner", "manager"]);
  const canShareWhatsApp = userHasAnyRole(["owner", "manager"]);

  const notAvailable = t("studio.products.common.notAvailable");
  const generalCategoryLabel = t("studio.catalog.general");

  useEffect(() => {
    if (searchParams.get("import") === "1") {
      setImportModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("import");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("catalog_missing_photo") === "1") {
      setMissingPhotoOnly(true);
      toast.info(t("studio.products.catalogSync.missingPhotoToast"));
      const next = new URLSearchParams(searchParams);
      next.delete("catalog_missing_photo");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await getMetaCommerceStatus();
        if (response.data?.installed && response.data.enabled) {
          setCatalogMissingPhotoCount(response.data.counts?.missing_photo ?? 0);
        } else {
          setCatalogMissingPhotoCount(0);
        }
      } catch {
        setCatalogMissingPhotoCount(0);
      }
    })();
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("studio.products.validation.unitRequired")),
        name: Yup.string().trim().required(t("studio.products.validation.nameRequired")),
        brand: Yup.string()
          .trim()
          .max(255, t("studio.products.validation.brandMax"))
          .notRequired(),
        supplier_name: Yup.string()
          .trim()
          .max(255, t("studio.products.validation.supplierMax"))
          .notRequired(),
        product_type: Yup.string()
          .oneOf(["retail", "internal"])
          .required(t("studio.products.validation.typeRequired")),
        unit_of_measure: Yup.string()
          .oneOf(["unit", "ml", "g"])
          .required(t("studio.products.validation.measureRequired")),
        current_stock: Yup.number()
          .typeError(t("studio.products.validation.stockType"))
          .required(t("studio.products.validation.stockRequired")),
        minimum_stock: Yup.number()
          .typeError(t("studio.products.validation.minimumStockType"))
          .min(0, t("studio.products.validation.minimumStockMin"))
          .required(t("studio.products.validation.minimumStockRequired")),
        sale_price_display: Yup.string().when("product_type", {
          is: "retail",
          then: (schema) =>
            schema
              .required(t("studio.products.validation.priceRequired"))
              .test("price-valid", t("studio.products.validation.priceInvalid"), (value) => {
                return parseMoneyInputToCents(value || "", i18n.language) > 0;
              }),
          otherwise: (schema) => schema.notRequired(),
        }),
        cost_price_display: Yup.string().test(
          "cost-valid",
          t("studio.products.validation.costInvalid"),
          (value) =>
            !value ||
            value.trim() === "" ||
            parseMoneyInputToCents(value, i18n.language) >= 0
        ),
        ...(useCatalogCategories
          ? {
              category_id: Yup.string().nullable().notRequired(),
            }
          : {}),
      }),
    [t, i18n.language, useCatalogCategories]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBrandFilter(brandFilter.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [brandFilter]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSupplierFilter(supplierFilter.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [supplierFilter]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedBrandFilter, debouncedSupplierFilter, unitFilter, expiringOnly, openedOnly]);

  const loadUnits = useCallback(async () => {
    try {
      const response = await getUnits({ per_page: UNITS_PAGE_SIZE });
      setUnits(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.loadError"));
    }
  }, [t]);

  const loadCatalogConfig = useCallback(async () => {
    try {
      const settingsResponse = await getSettings();
      const enabled = settingsResponse.data.use_catalog_categories;
      setTenantId(settingsResponse.data.tenant_id);
      setUseCatalogCategories(enabled);

      if (!enabled) {
        setCategories([]);
        return;
      }

      const categoriesResponse = await getCatalogCategories({
        per_page: CATEGORIES_PAGE_SIZE,
        is_active: true,
      });
      setCategories(categoriesResponse.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.loadError"));
    }
  }, [t]);

  const resolveCategoryOptions = useCallback(
    (currentCategory?: CatalogCategory | null): CatalogCategory[] => {
      if (!currentCategory) {
        return categories;
      }

      if (categories.some((category) => category.id === currentCategory.id)) {
        return categories;
      }

      return [...categories, currentCategory];
    },
    [categories]
  );

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const baseParams = {
        ...(unitFilter ? { unit_id: Number(unitFilter) } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(debouncedBrandFilter ? { brand: debouncedBrandFilter } : {}),
        ...(debouncedSupplierFilter ? { supplier_name: debouncedSupplierFilter } : {}),
        ...(expiringOnly ? { expiring_within_days: 30 } : {}),
        ...(openedOnly ? { has_opened_batches: 1 } : {}),
      };

      const withoutPhoto = (items: Product[]) =>
        missingPhotoOnly
          ? items.filter(
              (item) =>
                item.product_type === "retail" &&
                !item.image_url &&
                !item.image_path
            )
          : items;

      if (useCatalogCategories || missingPhotoOnly) {
        const allProducts = await fetchAllPaginated((currentPage, perPage) =>
          getProducts({
            ...baseParams,
            page: currentPage,
            per_page: perPage,
          })
        );
        setProducts(withoutPhoto(allProducts));
        setMeta(null);
        return;
      }

      const response = await getProducts({
        ...baseParams,
        page,
        per_page: PER_PAGE,
      });
      setProducts(withoutPhoto(response.data));
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    debouncedBrandFilter,
    debouncedSupplierFilter,
    unitFilter,
    expiringOnly,
    openedOnly,
    missingPhotoOnly,
    t,
    useCatalogCategories,
  ]);

  const handleShareWhatsApp = async (product: Product) => {
    try {
      const response = await getMetaCommerceProductMessage(product.id);
      const text = response.data.text;
      const copied = await copyTextToClipboard(text);
      if (copied) {
        toast.success(t("studio.products.toast.whatsappMessageCopied"));
      }
      window.open(response.data.whatsapp_url, "_blank", "noopener,noreferrer");
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.whatsappMessageError"));
    }
  };

  useEffect(() => {
    loadUnits();
    loadCatalogConfig();
  }, [loadUnits, loadCatalogConfig]);

  const handleCategoryWizardComplete = useCallback(async () => {
    await loadCatalogConfig();
    await fetchProducts();
  }, [fetchProducts, loadCatalogConfig]);

  const {
    showBanner: showCategoryBanner,
    categoryWizardOpen,
    openWizard: openCategoryWizard,
    closeWizard: closeCategoryWizard,
    dismissBanner: dismissCategoryBanner,
    completeWizard: completeCategoryWizard,
  } = useCatalogCategoryOnboarding({
    tenantId,
    useCatalogCategories,
    canConfigure: canConfigureCategories,
    onWizardComplete: handleCategoryWizardComplete,
  });

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const formInitialValues = useMemo(
    () =>
      editingProduct
        ? mapProductToFormValues(editingProduct, i18n.language)
        : { ...emptyFormValues(), unit_id: unitFilter || "" },
    [editingProduct, unitFilter, i18n.language]
  );

  const hasNegativeMargin = useCallback(
    (values: ProductFormValues): boolean => {
      if (values.product_type !== "retail") {
        return false;
      }

      const saleCents = parseMoneyInputToCents(values.sale_price_display, i18n.language);
      const costCents = parseMoneyInputToCents(values.cost_price_display, i18n.language);

      return saleCents > 0 && costCents > 0 && costCents > saleCents;
    },
    [i18n.language]
  );

  const clearPendingPhoto = () => {
    setPendingPhotoFile(null);
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhotoPreviewUrl(null);
  };

  const saveProduct = async (values: ProductFormValues) => {
    setSubmitting(true);
    const payload = buildProductPayload(values, useCatalogCategories, i18n.language);

    try {
      if (editingProduct) {
        const response = await updateProduct(editingProduct.id, payload);
        toast.success(response.message);
      } else {
        const response = await createProduct(payload);
        toast.success(response.message);
        if (pendingPhotoFile && response.data?.id) {
          try {
            await uploadProductPhoto(response.data.id, pendingPhotoFile);
          } catch {
            toast.warning(t("studio.products.toast.photoUploadDeferredError"));
          }
        }
      }
      clearPendingPhoto();
      closeOffcanvas();
      if (!editingProduct && page !== 1) {
        setPage(1);
      } else {
        await fetchProducts();
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoPick = async (file: File) => {
    if (!editingProduct) {
      if (photoPreviewUrl) {
        URL.revokeObjectURL(photoPreviewUrl);
      }
      setPendingPhotoFile(file);
      setPhotoPreviewUrl(URL.createObjectURL(file));
      return;
    }

    setPhotoBusy(true);
    try {
      const response = await uploadProductPhoto(editingProduct.id, file);
      setEditingProduct(response.data);
      toast.success(response.message || t("studio.products.toast.photoUploadSuccess"));
      await fetchProducts();
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.photoUploadError"));
    } finally {
      setPhotoBusy(false);
    }
  };

  const handlePhotoClear = async () => {
    if (!editingProduct) {
      clearPendingPhoto();
      return;
    }

    if (!editingProduct.image_url && !editingProduct.image_path) {
      return;
    }

    setPhotoBusy(true);
    try {
      const response = await deleteProductPhoto(editingProduct.id);
      setEditingProduct(response.data);
      toast.success(response.message || t("studio.products.toast.photoRemoveSuccess"));
      await fetchProducts();
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.photoRemoveError"));
    } finally {
      setPhotoBusy(false);
    }
  };

  const resolveCatalogSyncBadge = (
    product: Product
  ): { color: string; label: string } | null => {
    if (product.product_type !== "retail") {
      return null;
    }

    const label = (product.catalog_sync_label ||
      (product.image_url || product.image_path ? "pending" : "missing_photo")) as CatalogSyncLabel;

    // missing_photo uses the generic thumbnail + caption in the name cell
    if (label === "missing_photo") {
      return null;
    }

    const map: Record<Exclude<CatalogSyncLabel, "missing_photo">, { color: string; labelKey: string }> = {
      synced: { color: "success", labelKey: "studio.products.catalogSync.synced" },
      pending: { color: "warning", labelKey: "studio.products.catalogSync.pending" },
      error: { color: "danger", labelKey: "studio.products.catalogSync.error" },
      skipped: { color: "light", labelKey: "studio.products.catalogSync.skipped" },
    };

    const entry = map[label as Exclude<CatalogSyncLabel, "missing_photo">] ?? map.pending;
    return { color: entry.color, label: t(entry.labelKey) };
  };

  const formik = useFormik<ProductFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (hasNegativeMargin(values)) {
        setPendingSubmitValues(values);
        setNegativeMarginModal(true);
        return;
      }

      await saveProduct(values);
    },
  });

  const profitMargin = useMemo(() => {
    if (formik.values.product_type !== "retail") {
      return null;
    }

    const saleCents = parseMoneyInputToCents(formik.values.sale_price_display, i18n.language);
    const costCents = parseMoneyInputToCents(formik.values.cost_price_display, i18n.language);

    if (saleCents <= 0 || costCents <= 0) {
      return null;
    }

    return {
      ratio: (saleCents - costCents) / saleCents,
      profitCents: saleCents - costCents,
    };
  }, [
    formik.values.product_type,
    formik.values.sale_price_display,
    formik.values.cost_price_display,
    i18n.language,
  ]);

  const confirmNegativeMargin = async () => {
    if (!pendingSubmitValues) {
      return;
    }

    setNegativeMarginModal(false);
    const values = pendingSubmitValues;
    setPendingSubmitValues(null);
    await saveProduct(values);
  };

  const cancelNegativeMargin = () => {
    setNegativeMarginModal(false);
    setPendingSubmitValues(null);
  };

  const closeOffcanvas = () => {
    setOffcanvasOpen(false);
    setEditingProduct(null);
    setFormLoading(false);
    setProductFormTab("data");
    clearPendingPhoto();
  };

  const openCreate = () => {
    setEditingProduct(null);
    setFormLoading(false);
    setProductFormTab("data");
    clearPendingPhoto();
    setOffcanvasOpen(true);
  };

  const openEdit = async (product: Product, tab: ProductFormTab = "data") => {
    setEditingProduct(null);
    setFormLoading(true);
    setProductFormTab(tab);
    clearPendingPhoto();
    setOffcanvasOpen(true);

    try {
      const response = await getProduct(product.id);
      setEditingProduct(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.loadError"));
      closeOffcanvas();
    } finally {
      setFormLoading(false);
    }
  };

  const openStockHistory = (product: Product) => {
    openEdit(product, "stock");
  };

  const handleProductFormTabChange = (tab: ProductFormTab) => {
    setProductFormTab(tab);
  };

  const handleStockAdjusted = async () => {
    if (!editingProduct) {
      await fetchProducts();
      return;
    }

    try {
      const response = await getProduct(editingProduct.id);
      setEditingProduct(response.data);
      setProducts((current) =>
        current.map((product) =>
          product.id === response.data.id ? response.data : product
        )
      );
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.loadError"));
      await fetchProducts();
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await deleteProduct(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);
      const isLastOnPage = products.length === 1 && page > 1;
      if (isLastOnPage) {
        setPage(page - 1);
      } else {
        await fetchProducts();
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.toast.deleteError"));
    } finally {
      setSubmitting(false);
    }
  };

  const resolveUnitName = (unitId: number): string =>
    units.find((unit) => unit.id === unitId)?.name ?? notAvailable;

  const resolveTypeLabel = (type: ProductType): string =>
    type === "retail"
      ? t("studio.products.type.retail")
      : t("studio.products.type.internal");

  const resolveMeasureLabel = (measure: UnitOfMeasure): string =>
    t(`studio.products.measure.${measure}`);

  const resolveProductCategoryLabel = (product: Product): string =>
    resolveCategoryLabel(product.category, generalCategoryLabel);

  const categoryOptions = resolveCategoryOptions(editingProduct?.category ?? null);

  const groupedProducts = useMemo(() => {
    if (!useCatalogCategories) {
      return null;
    }

    return groupCatalogItemsByCategory(products, categories, generalCategoryLabel);
  }, [categories, generalCategoryLabel, products, useCatalogCategories]);

  const productsTableColumnCount =
    9 + (useCatalogCategories && !groupedProducts ? 1 : 0);

  const renderProductRow = (product: Product) => {
    const isLowStock = product.current_stock <= product.minimum_stock;
    const measureLabel = resolveMeasureLabel(product.unit_of_measure);
    const productMargin = resolveProductMargin(product);
    const catalogMeta = resolveProductCatalogMeta(product);

    return (
      <tr key={product.id}>
        <td>
          <div className="d-flex align-items-center gap-2">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt=""
                width={40}
                height={40}
                className="rounded object-fit-cover flex-shrink-0"
              />
            ) : (
              <div
                className="rounded border bg-light-subtle text-muted d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 40, height: 40 }}
                title={t("studio.products.table.noPhoto")}
                aria-label={t("studio.products.table.noPhoto")}
              >
                <i className="ri-image-line fs-5" />
              </div>
            )}
            <div>
              <div>{product.name}</div>
              {catalogMeta ? <div className="text-muted small">{catalogMeta}</div> : null}
              {!product.image_url ? (
                <div className="text-muted small mt-1">{t("studio.products.table.noPhoto")}</div>
              ) : null}
              {(() => {
                const syncBadge = resolveCatalogSyncBadge(product);
                return syncBadge ? (
                  <Badge color={syncBadge.color} className="mt-1" pill>
                    {syncBadge.label}
                  </Badge>
                ) : null;
              })()}
            </div>
          </div>
        </td>
        <td>{resolveUnitName(product.unit_id)}</td>
        <td>
          <Badge color={product.product_type === "retail" ? "primary" : "info"}>
            {resolveTypeLabel(product.product_type)}
          </Badge>
        </td>
        {useCatalogCategories && !groupedProducts ? (
          <td>{resolveProductCategoryLabel(product)}</td>
        ) : null}
        <td>{measureLabel}</td>
        <td>
          <span
            className={`badge ${
              isLowStock ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success"
            }`}
            title={
              isLowStock
                ? t("studio.products.table.lowStockHint", {
                    minimum: product.minimum_stock,
                    measure: measureLabel,
                  })
                : undefined
            }
          >
            {t("studio.products.table.stockValue", {
              stock: product.current_stock,
              measure: measureLabel,
            })}
          </span>
          {product.expiring_within_days != null && product.expiring_within_days < 0 ? (
            <Badge color="danger" className="ms-1">
              {t("studio.products.expiry.expired")}
            </Badge>
          ) : product.expiring_within_days != null && product.expiring_within_days <= 30 ? (
            <Badge
              color={product.expiring_within_days <= 7 ? "warning" : "info"}
              className="ms-1"
            >
              {t("studio.products.expiry.expiresInDays", {
                days: product.expiring_within_days,
              })}
            </Badge>
          ) : (
            <Badge color="light" className="ms-1 text-muted">
              {t("studio.products.expiry.noExpiry")}
            </Badge>
          )}
          {product.open_expiring_within_days != null &&
          product.open_expiring_within_days < 0 ? (
            <Badge color="danger" className="ms-1">
              {t("studio.products.openShelf.pastUseBy")}
            </Badge>
          ) : product.open_expiring_within_days != null &&
            product.open_expiring_within_days <= 7 ? (
            <Badge
              color={product.open_expiring_within_days <= 3 ? "warning" : "info"}
              className="ms-1"
            >
              {t("studio.products.openShelf.useWithinDays", {
                days: product.open_expiring_within_days,
              })}
            </Badge>
          ) : product.has_opened_batches ? (
            <Badge color="secondary" className="ms-1">
              {t("studio.products.openShelf.opened")}
            </Badge>
          ) : null}
        </td>
        <td>
          {product.sale_price !== null ? formatMoney(product.sale_price) : notAvailable}
        </td>
        <td>
          {product.cost_price_cents !== null
            ? formatMoney(product.cost_price_cents)
            : notAvailable}
        </td>
        <td>
          {productMargin === null ? (
            notAvailable
          ) : (
            <span
              className={`badge ${
                productMargin < 0 ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success"
              }`}
            >
              {formatPercent(productMargin)}
            </span>
          )}
        </td>
        <td className="text-end">
          {canShareWhatsApp && product.product_type === "retail" ? (
            <Button
              color="soft-success"
              size="sm"
              className="me-1"
              title={t("studio.products.actions.shareWhatsApp")}
              onClick={() => void handleShareWhatsApp(product)}
            >
              <i className="ri-whatsapp-line" />
            </Button>
          ) : null}
          <Button
            color="soft-info"
            size="sm"
            className="me-1"
            title={t("studio.products.actions.stockHistory")}
            onClick={() => openStockHistory(product)}
          >
            <i className="ri-history-line" />
          </Button>
          <Button
            color="soft-primary"
            size="sm"
            className="me-1"
            title={t("studio.products.actions.edit")}
            onClick={() => openEdit(product)}
          >
            <i className="ri-pencil-line" />
          </Button>
          <Button
            color="soft-danger"
            size="sm"
            title={t("studio.products.actions.delete")}
            onClick={() => {
              setDeleteTarget(product);
              setDeleteModal(true);
            }}
          >
            <i className="ri-delete-bin-line" />
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.products.breadcrumb.title")}
          pageTitle={t("studio.products.breadcrumb.pageTitle")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                <h4 className="card-title mb-0 flex-grow-1">{t("studio.products.card.title")}</h4>
                <HelpArticleLink articlePath={HELP_ARTICLE_PRODUCTS} className="align-self-center" />
                <Button color="soft-primary" tag={Link} to="/products/valuation">
                  {t("studio.products.actions.valuation")}
                </Button>
                <Button color="soft-primary" onClick={() => setNfeImportOpen(true)}>
                  <i className="ri-file-code-line align-bottom me-1" />
                  {t("studio.products.actions.nfeImport")}
                </Button>
                <Button color="soft-primary" onClick={() => setImportModalOpen(true)}>
                  <i className="ri-upload-2-line align-bottom me-1" />
                  {t("imports.wizard.button.products")}
                </Button>
                <Button color="primary" onClick={openCreate}>
                  <i className="ri-add-line align-bottom me-1" />
                  {t("studio.products.actions.new")}
                </Button>
              </CardHeader>
              <CardBody>
                {!useCatalogCategories && showCategoryBanner ? (
                  <CatalogCategoryOnboardingBanner
                    canConfigure={canConfigureCategories}
                    onStartWizard={openCategoryWizard}
                    onDismiss={dismissCategoryBanner}
                  />
                ) : null}

                {catalogMissingPhotoCount > 0 ? (
                  <Alert color="warning" className="mb-3">
                    {t("studio.products.catalogSync.missingPhotoBanner", {
                      count: catalogMissingPhotoCount,
                    })}{" "}
                    <Button
                      color="link"
                      className="p-0 align-baseline"
                      onClick={() => {
                        setMissingPhotoOnly(true);
                        setPage(1);
                      }}
                    >
                      {t("studio.products.catalogSync.missingPhotoCta")}
                    </Button>
                  </Alert>
                ) : null}

                <Row className="g-2 mb-3">
                  <Col md={4}>
                    <Input
                      type="select"
                      value={unitFilter}
                      onChange={(event) => setUnitFilter(event.target.value)}
                      aria-label={t("studio.products.filter.unit")}
                    >
                      <option value="">{t("studio.products.filter.allUnits")}</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={8}>
                    <Input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder={t("studio.products.search.placeholder")}
                    />
                  </Col>
                  <Col md={6}>
                    <Input
                      type="search"
                      value={brandFilter}
                      onChange={(event) => setBrandFilter(event.target.value)}
                      placeholder={t("studio.products.filter.brandPlaceholder")}
                      aria-label={t("studio.products.filter.brand")}
                    />
                  </Col>
                  <Col md={6}>
                    <Input
                      type="search"
                      value={supplierFilter}
                      onChange={(event) => setSupplierFilter(event.target.value)}
                      placeholder={t("studio.products.filter.supplierPlaceholder")}
                      aria-label={t("studio.products.filter.supplier")}
                    />
                  </Col>
                  <Col md={12}>
                    <div className="form-check">
                      <Input
                        id="products-expiring-filter"
                        type="checkbox"
                        className="form-check-input"
                        checked={expiringOnly}
                        onChange={(event) => setExpiringOnly(event.target.checked)}
                      />
                      <Label className="form-check-label" htmlFor="products-expiring-filter">
                        {t("studio.products.filter.expiringWithin30")}
                      </Label>
                    </div>
                    <div className="form-check mt-2">
                      <Input
                        id="products-opened-filter"
                        type="checkbox"
                        className="form-check-input"
                        checked={openedOnly}
                        onChange={(event) => {
                          setOpenedOnly(event.target.checked);
                          setPage(1);
                        }}
                      />
                      <Label className="form-check-label" htmlFor="products-opened-filter">
                        {t("studio.products.filter.openedOnly")}
                      </Label>
                    </div>
                    <div className="form-check mt-2">
                      <Input
                        id="products-missing-photo-filter"
                        type="checkbox"
                        className="form-check-input"
                        checked={missingPhotoOnly}
                        onChange={(event) => {
                          setMissingPhotoOnly(event.target.checked);
                          setPage(1);
                        }}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="products-missing-photo-filter"
                      >
                        {t("studio.products.filter.missingPhotoWhatsApp")}
                      </Label>
                    </div>
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary">{t("studio.products.loading")}</Spinner>
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">
                      {debouncedSearch
                        ? t("studio.products.empty.noResults", { term: debouncedSearch })
                        : t("studio.products.empty.noData")}
                    </p>
                    <Button color="primary" onClick={openCreate}>
                      {t("studio.products.actions.createFirst")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.products.table.name")}</th>
                            <th>{t("studio.products.table.unit")}</th>
                            <th>{t("studio.products.table.type")}</th>
                            {useCatalogCategories && !groupedProducts ? (
                              <th>{t("studio.catalog.category")}</th>
                            ) : null}
                            <th>{t("studio.products.table.measure")}</th>
                            <th>{t("studio.catalog.products.fields.current_stock")}</th>
                            <th>{t("studio.products.table.price")}</th>
                            <th>{t("studio.products.table.cost")}</th>
                            <th>{t("studio.products.table.margin")}</th>
                            <th className="text-end">{t("studio.products.table.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedProducts
                            ? groupedProducts.flatMap((group) => [
                                <tr key={`group-${group.groupKey}`} className="table-light">
                                  <td colSpan={productsTableColumnCount}>
                                    <span className="fw-semibold">{group.label}</span>
                                    <span className="text-muted ms-2 small">
                                      {t("studio.catalog.onboarding.groupCountProducts", {
                                        count: group.services.length,
                                      })}
                                    </span>
                                  </td>
                                </tr>,
                                ...group.services.map((product) => renderProductRow(product)),
                              ])
                            : products.map((product) => renderProductRow(product))}
                        </tbody>
                      </Table>
                    </div>
                    {meta && meta.last_page > 1 && !useCatalogCategories ? (
                      <Row className="align-items-center mt-3">
                        <Col sm={6}>
                          <div className="text-muted">
                            {t("studio.products.pagination.showing", {
                              from: meta.from ?? 0,
                              to: meta.to ?? 0,
                              total: meta.total,
                            })}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <Pagination className="pagination-separated justify-content-sm-end mb-0">
                            <PaginationItem disabled={page <= 1}>
                              <PaginationLink
                                previous
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page > 1) {
                                    setPage(page - 1);
                                  }
                                }}
                              >
                                {t("studio.products.pagination.previous")}
                              </PaginationLink>
                            </PaginationItem>
                            <PaginationItem disabled={page >= meta.last_page}>
                              <PaginationLink
                                next
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page < meta.last_page) {
                                    setPage(page + 1);
                                  }
                                }}
                              >
                                {t("studio.products.pagination.next")}
                              </PaginationLink>
                            </PaginationItem>
                          </Pagination>
                        </Col>
                      </Row>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas isOpen={offcanvasOpen} toggle={closeOffcanvas} direction="end" className="w-50">
        <OffcanvasHeader toggle={closeOffcanvas}>
          {editingProduct
            ? productFormTab === "stock"
              ? t("studio.products.stockHistory.offcanvasTitle", {
                  name: editingProduct.name,
                })
              : productFormTab === "batches"
                ? t("studio.products.batches.offcanvasTitle", {
                    name: editingProduct.name,
                  })
                : t("studio.products.form.editTitle")
            : t("studio.products.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("studio.products.loading")}</Spinner>
            </div>
          ) : (
            <>
              {editingProduct ? (
                <Nav pills className="nav-pills-custom nav-primary gap-2 mb-4">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={productFormTab === "data" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        handleProductFormTabChange("data");
                      }}
                    >
                      {t("studio.products.form.tabs.data")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={productFormTab === "stock" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        handleProductFormTabChange("stock");
                      }}
                    >
                      {t("studio.products.form.tabs.stockHistory")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={productFormTab === "batches" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        handleProductFormTabChange("batches");
                      }}
                    >
                      {t("studio.products.form.tabs.batches")}
                    </NavLink>
                  </NavItem>
                </Nav>
              ) : null}

              {productFormTab === "stock" && editingProduct ? (
                <ProductStockHistoryPanel
                  key={editingProduct.id}
                  productId={editingProduct.id}
                  unitOfMeasure={editingProduct.unit_of_measure}
                  costPriceCents={editingProduct.cost_price_cents}
                  currentStock={editingProduct.current_stock}
                  canAdjust={canAdjustStock}
                  onStockAdjusted={handleStockAdjusted}
                />
              ) : productFormTab === "batches" && editingProduct ? (
                <ProductBatchesPanel
                  key={`batches-${editingProduct.id}`}
                  productId={editingProduct.id}
                  openShelfLifeDays={
                    editingProduct.open_shelf_life_days ??
                    (formik.values.open_shelf_life_days.trim() !== ""
                      ? Number(formik.values.open_shelf_life_days)
                      : null)
                  }
                  canEdit={canAdjustStock}
                  onChanged={handleStockAdjusted}
                />
              ) : (
          <Form key={entityFormKey(editingProduct?.id)} onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <Label htmlFor="product-unit">{t("studio.products.fields.unit.label")}</Label>
              <Input
                id="product-unit"
                name="unit_id"
                type="select"
                value={formik.values.unit_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.unit_id && formik.errors.unit_id)}
              >
                <option value="">{t("studio.products.fields.unit.placeholder")}</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Input>
              <FormFeedback>{formik.errors.unit_id}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="product-name">{t("studio.products.fields.name.label")}</Label>
              <Input
                id="product-name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.products.fields.name.placeholder")}
                invalid={!!(formik.touched.name && formik.errors.name)}
              />
              <FormFeedback>{formik.errors.name}</FormFeedback>
            </div>
            {formik.values.product_type === "retail" ? (
              <div className="mb-3">
                <Label htmlFor="product-photo">
                  {t("studio.products.fields.photo.label")}
                </Label>
                <p className="text-muted small mb-2">
                  {t("studio.products.fields.photo.help")}
                </p>
                {(photoPreviewUrl || editingProduct?.image_url) && (
                  <div className="mb-2">
                    <img
                      src={photoPreviewUrl || editingProduct?.image_url || ""}
                      alt=""
                      width={96}
                      height={96}
                      className="rounded border object-fit-cover"
                    />
                  </div>
                )}
                <Input
                  id="product-photo"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={photoBusy || submitting}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      void handlePhotoPick(file);
                    }
                    event.target.value = "";
                  }}
                />
                {(photoPreviewUrl ||
                  editingProduct?.image_url ||
                  editingProduct?.image_path) && (
                  <Button
                    color="link"
                    size="sm"
                    className="px-0 mt-1"
                    disabled={photoBusy}
                    onClick={() => void handlePhotoClear()}
                  >
                    {t("studio.products.fields.photo.remove")}
                  </Button>
                )}
              </div>
            ) : null}
            <Row>
              <Col md={6} className="mb-3">
                <Label htmlFor="product-brand">{t("studio.products.fields.brand.label")}</Label>
                <Input
                  id="product-brand"
                  name="brand"
                  value={formik.values.brand}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t("studio.products.fields.brand.placeholder")}
                  invalid={!!(formik.touched.brand && formik.errors.brand)}
                />
                <FormFeedback>{formik.errors.brand}</FormFeedback>
              </Col>
              <Col md={6} className="mb-3">
                <Label htmlFor="product-supplier">
                  {t("studio.products.fields.supplier.label")}
                </Label>
                <Input
                  id="product-supplier"
                  name="supplier_name"
                  value={formik.values.supplier_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t("studio.products.fields.supplier.placeholder")}
                  invalid={!!(formik.touched.supplier_name && formik.errors.supplier_name)}
                />
                <FormFeedback>{formik.errors.supplier_name}</FormFeedback>
              </Col>
            </Row>
            <div className="mb-3">
              <Label htmlFor="product-type">{t("studio.products.fields.type.label")}</Label>
              <Input
                id="product-type"
                name="product_type"
                type="select"
                value={formik.values.product_type}
                onChange={(event) => {
                  formik.handleChange(event);
                  if (event.target.value === "internal") {
                    formik.setFieldValue("sale_price_display", "");
                  }
                }}
                onBlur={formik.handleBlur}
              >
                <option value="retail">{t("studio.products.type.retail")}</option>
                <option value="internal">{t("studio.products.type.internal")}</option>
              </Input>
            </div>
            <div className="mb-3">
              <Label htmlFor="product-measure">{t("studio.products.fields.measure.label")}</Label>
              <Input
                id="product-measure"
                name="unit_of_measure"
                type="select"
                value={formik.values.unit_of_measure}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="unit">{t("studio.products.measure.unit")}</option>
                <option value="ml">{t("studio.products.measure.ml")}</option>
                <option value="g">{t("studio.products.measure.g")}</option>
              </Input>
            </div>
            {useCatalogCategories ? (
              <div className="mb-3">
                <Label htmlFor="product-category">{t("studio.catalog.category")}</Label>
                <Input
                  id="product-category"
                  name="category_id"
                  type="select"
                  value={formik.values.category_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">{t("studio.catalog.general")}</option>
                  {categoryOptions.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Input>
              </div>
            ) : null}
            <Row>
              <Col md={6} className="mb-3">
                <Label htmlFor="product-stock">{t("studio.products.fields.stock.label")}</Label>
                <Input
                  id="product-stock"
                  name="current_stock"
                  type="number"
                  value={formik.values.current_stock}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.current_stock && formik.errors.current_stock)}
                />
                <FormFeedback>{formik.errors.current_stock}</FormFeedback>
              </Col>
              <Col md={6} className="mb-3">
                <Label htmlFor="product-min-stock">
                  {t("studio.products.fields.minimumStock.label")}
                </Label>
                <Input
                  id="product-min-stock"
                  name="minimum_stock"
                  type="number"
                  min={0}
                  value={formik.values.minimum_stock}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.minimum_stock && formik.errors.minimum_stock)}
                />
                <FormFeedback>{formik.errors.minimum_stock}</FormFeedback>
              </Col>
            </Row>
            <div className="mb-3">
              <Label htmlFor="product-cost">{t("studio.products.fields.cost.label")}</Label>
              <MoneyInput
                id="product-cost"
                locale={i18n.language}
                value={formik.values.cost_price_display}
                onChange={(value) => formik.setFieldValue("cost_price_display", value)}
                onBlur={() => formik.setFieldTouched("cost_price_display", true)}
                placeholder={t("studio.products.fields.cost.placeholder")}
                invalid={
                  !!(formik.touched.cost_price_display && formik.errors.cost_price_display)
                }
              />
              {formik.touched.cost_price_display && formik.errors.cost_price_display ? (
                <div className="invalid-feedback d-block">
                  {formik.errors.cost_price_display}
                </div>
              ) : null}
              <small className="text-muted">{t("studio.products.fields.cost.hint")}</small>
            </div>
            <div className="mb-3">
              <Label htmlFor="product-open-shelf-life">
                {t("studio.products.fields.openShelfLife.label")}
              </Label>
              <Input
                id="product-open-shelf-life"
                name="open_shelf_life_days"
                type="number"
                min={1}
                max={3650}
                value={formik.values.open_shelf_life_days}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.products.fields.openShelfLife.placeholder")}
              />
              <small className="text-muted d-block">
                {t("studio.products.fields.openShelfLife.hint")}
              </small>
            </div>
            {formik.values.product_type === "retail" && (
              <div className="mb-3">
                <Label htmlFor="product-price">{t("studio.products.fields.price.label")}</Label>
                <MoneyInput
                  id="product-price"
                  locale={i18n.language}
                  value={formik.values.sale_price_display}
                  onChange={(value) => formik.setFieldValue("sale_price_display", value)}
                  onBlur={() => formik.setFieldTouched("sale_price_display", true)}
                  placeholder={t("studio.products.fields.price.placeholder")}
                  invalid={
                    !!(formik.touched.sale_price_display && formik.errors.sale_price_display)
                  }
                />
                {formik.touched.sale_price_display && formik.errors.sale_price_display ? (
                  <div className="invalid-feedback d-block">
                    {formik.errors.sale_price_display}
                  </div>
                ) : null}
              </div>
            )}
            {profitMargin !== null && (
              <div className="mb-3">
                <span className={profitMargin.ratio < 0 ? "text-danger" : "text-success"}>
                  {t("studio.products.fields.margin.summary", {
                    margin: formatPercent(profitMargin.ratio),
                    profit: formatMoney(profitMargin.profitCents),
                  })}
                </span>
              </div>
            )}
            <div className="d-flex gap-2 justify-content-end">
              <Button color="light" onClick={closeOffcanvas}>
                {t("studio.products.actions.cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? t("studio.products.loading") : t("studio.products.actions.save")}
              </Button>
            </div>
          </Form>
              )}
            </>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <h4>{t("studio.products.delete.title")}</h4>
            <p className="text-muted mx-4 mb-0">
              {deleteTarget
                ? t("studio.products.delete.description", { name: deleteTarget.name })
                : t("studio.products.delete.fallback")}
            </p>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("studio.products.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete} disabled={submitting}>
              {t("studio.products.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={negativeMarginModal} toggle={cancelNegativeMargin} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="text-warning display-6 mb-2">
              <i className="ri-error-warning-line" />
            </div>
            <h4>{t("studio.products.negativeMargin.title")}</h4>
            <p className="text-muted mx-4 mb-0">
              {t("studio.products.negativeMargin.description")}
            </p>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4">
            <Button color="light" onClick={cancelNegativeMargin}>
              {t("studio.products.actions.cancel")}
            </Button>
            <Button color="warning" onClick={confirmNegativeMargin} disabled={submitting}>
              {t("studio.products.negativeMargin.confirm")}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <CatalogCategoryWizard
        isOpen={categoryWizardOpen}
        onClose={closeCategoryWizard}
        onComplete={completeCategoryWizard}
      />

      <SmartImportWizard
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        entityType="products"
        onCompleted={() => {
          void fetchProducts();
        }}
      />

      <ProductNfeImportOffcanvas
        isOpen={nfeImportOpen}
        units={units}
        preferredUnitId={unitFilter}
        onClose={() => setNfeImportOpen(false)}
        onImported={() => {
          void fetchProducts();
        }}
      />
    </div>
  );
};

export default ProductsList;
